import { findOnlyOrThrow } from "@dhau/lang-extras";
import type { Country } from "~/frontend-common/localisation.ts";
import type { AppState } from "~/store/root-reducer.ts";
import type { ShopifyPrices } from "~/features/reference/shopify-prices.ts";
import { isOnceOffLoaded } from "~/utils/loading.ts";
import type { UserStatus } from "./store-slice.ts";
import type { AppAuthUser } from "./app-auth-user.ts";

function selectUser(state: AppState): UserStatus {
	return state.user.user;
}

function selectJustSignedUp(state: AppState) {
	return state.user.justSignedUp;
}

function selectSignedInUser(state: AppState): AppAuthUser {
	const user = selectUser(state);
	if (user.type !== "signedIn") {
		// return null
		throw new Error("Not signed in");
	}
	return user.user;
}

function isAdminUser(state: AppState): boolean {
	const user = selectUser(state);
	if (user.type !== "signedIn") {
		return false;
	}
	return user.user.isAdminAccount;
}

// Used to allow image downloads with no logos
// Could have added the emails to the admin list but this gives separation in case isAdminUser is used
// for other purposes in future
function isAuthorisedPartner({
	user: userState,
	reference: { siteCode },
}: AppState): boolean {
	const user = userState.user;
	if (user.type !== "signedIn") {
		return false;
	}

	const emailAddress = user.user.emailAddress;
	// US-only partner domains for special features
	const usPartnerDomains = ["johnsboxofbricks.com", "hdddistribution.com"];

	// Only apply to US site
	if (siteCode.toLowerCase() !== "us") {
		return false;
	}

	return usPartnerDomains.some((domain) => emailAddress.endsWith(domain));
}

function selectCountry({
	user: { selectedCountryCode },
	reference: { countries },
}: AppState): Country {
	if (!isOnceOffLoaded(countries)) {
		throw new Error("Countries not loaded");
	}
	return findOnlyOrThrow(
		countries.data,
		(c) => c.code === selectedCountryCode,
		`Country not found for code ${selectedCountryCode}`,
	);
}

function selectHasCountries({ reference: { countries } }: AppState): boolean {
	return isOnceOffLoaded(countries);
}

function selectCountries(state: AppState) {
	const {
		reference: { countries },
	} = state;
	if (!isOnceOffLoaded(countries)) {
		return [];
	}
	return countries.data;
}

function selectShopifyPricesForSelectedCountry({
	reference: { picPricesByCountryCode },
	user: { selectedCountryCode },
}: AppState): ShopifyPrices | undefined {
	if (!selectedCountryCode) {
		throw new Error("No selected country");
	}
	return picPricesByCountryCode[selectedCountryCode];
}

function selectMeasurementsPreference(state: AppState) {
	return state.user.measurementsPreference;
}

// Email verification selector
function selectNeedsEmailVerification(state: AppState): boolean {
	const user = selectUser(state);
	if (user.type !== "signedIn") {
		return false;
	}
	return !user.user.emailVerified;
}

export {
	selectHasCountries,
	selectCountries,
	isAdminUser,
	isAuthorisedPartner,
	selectCountry,
	selectSignedInUser,
	selectUser,
	selectJustSignedUp,
	selectMeasurementsPreference,
	selectShopifyPricesForSelectedCountry,
	selectNeedsEmailVerification,
};
